// Video validation utilities for poll uploads

export const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB (longer videos need more space)
export const MAX_VIDEO_DURATION = 60; // seconds
export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/mov'];

export interface VideoValidationResult {
  valid: boolean;
  error?: string;
  duration?: number;
}

/**
 * Get the duration of a video file in seconds
 */
export function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    // Timeout to prevent hanging forever (common on iOS with certain formats)
    const timeoutId = setTimeout(() => {
      URL.revokeObjectURL(video.src);
      video.src = '';
      // If we can't read duration, resolve with 0 and let it through
      // (the server/storage will handle oversized files)
      resolve(0);
    }, 8000);
    
    video.onloadedmetadata = () => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(video.src);
      // Handle Infinity duration (common with WebM/iOS recordings)
      if (!isFinite(video.duration) || video.duration <= 0) {
        resolve(0);
      } else {
        resolve(video.duration);
      }
    };
    
    video.onerror = () => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(video.src);
      // Don't reject — allow upload attempt even if metadata fails
      resolve(0);
    };
    
    video.src = URL.createObjectURL(file);
  });
}

/**
 * Extract a thumbnail from the first frame of a video
 * Uses proper event sequencing to ensure video is ready before capturing
 */
export function extractVideoThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'auto'; // Load more than just metadata
    video.muted = true;
    video.playsInline = true;
    
    // Timeout to prevent hanging forever
    const timeoutId = setTimeout(() => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Video thumbnail extraction timed out'));
    }, 15000); // 15 second timeout
    
    const captureFrame = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          clearTimeout(timeoutId);
          URL.revokeObjectURL(video.src);
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(
          (blob) => {
            clearTimeout(timeoutId);
            URL.revokeObjectURL(video.src);
            video.pause();
            video.src = '';
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create thumbnail blob'));
            }
          },
          'image/jpeg',
          0.8
        );
      } catch (err) {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to capture video frame'));
      }
    };
    
    video.onseeked = () => {
      // Give a small delay after seek for frame to render
      requestAnimationFrame(() => {
        captureFrame();
      });
    };
    
    video.onloadeddata = () => {
      // Video has enough data - try to seek to 0.1s to avoid black first frame
      if (video.duration > 0.2) {
        video.currentTime = 0.1;
      } else {
        // Very short video - capture first frame
        captureFrame();
      }
    };
    
    video.onerror = (e) => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(video.src);
      console.error('Video error during thumbnail extraction:', e);
      reject(new Error('Failed to load video for thumbnail'));
    };
    
    // For WebM files recorded in-browser, we need to handle them differently
    // as they may not have proper metadata
    video.oncanplay = () => {
      // If loadeddata hasn't fired yet but we can play, try seeking
      if (video.currentTime === 0 && video.duration > 0.2) {
        video.currentTime = 0.1;
      }
    };
    
    video.src = URL.createObjectURL(file);
    video.load(); // Explicitly call load to start loading
  });
}

/**
 * Validate a video file for poll upload
 */
export async function validateVideo(file: File): Promise<VideoValidationResult> {
  // Check file type - be lenient on iOS where type can be empty or unusual
  if (file.type && !ACCEPTED_VIDEO_TYPES.includes(file.type)) {
    // Also accept files that look like videos by extension
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['mp4', 'mov', 'webm', 'm4v'].includes(ext || '')) {
      return { 
        valid: false, 
        error: 'Please upload MP4, MOV, or WebM video' 
      };
    }
  }
  
  // Check file size
  if (file.size > MAX_VIDEO_SIZE) {
    const sizeMB = Math.round(file.size / 1024 / 1024);
    return { 
      valid: false, 
      error: `Video must be under ${Math.round(MAX_VIDEO_SIZE / 1024 / 1024)}MB (yours is ${sizeMB}MB)` 
    };
  }
  
  // Check duration (with fallback — if duration can't be read, allow it)
  try {
    const duration = await getVideoDuration(file);
    
    if (duration > 0 && duration > MAX_VIDEO_DURATION) {
      const durationSec = Math.round(duration);
      return { 
        valid: false, 
        error: `Video must be ${MAX_VIDEO_DURATION} seconds or less (yours is ${durationSec}s)` 
      };
    }
    
    return { valid: true, duration: duration || undefined };
  } catch (error) {
    console.error('Video validation error:', error);
    // Allow upload even if validation fails — don't block the user
    return { valid: true, duration: undefined };
  }
}

/**
 * Format duration in seconds to mm:ss
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format file size to human readable
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
